import type { ReactNode } from "react";
import type { Spans } from "../api";

/**
 * Emphasis (ADR-0205): the italics of a chapter, served beside its body as
 * byte spans and drawn over the text the readers already lay out — words,
 * taps and timings untouched. Latin is set in the reading face's real italic
 * (served from our origin, web/public/fonts). Arabic script is never slanted:
 * Vazirmatn has no italic, and a slant the browser makes up reads as a fault,
 * so emphasis there is a heavier weight instead. The app draws the same rule
 * (mobile/lib/player/emphasis.dart).
 */

const arabicLetter = /\p{Script=Arabic}/u;
const letter = /\p{L}/u;

/** One stretch of emphasised text in one script. */
export interface ScriptRun {
  text: string;
  arabic: boolean;
}

/**
 * scriptRuns cuts text where its letters change between Arabic script and any
 * other. A character that is not a letter — a space, a digit, punctuation, a
 * ZWNJ — joins the run before it; one before the first letter joins the first.
 */
export function scriptRuns(text: string): ScriptRun[] {
  const runs: ScriptRun[] = [];
  let pending = "";
  for (const ch of text) {
    if (!letter.test(ch)) {
      if (runs.length) runs[runs.length - 1]!.text += ch;
      else pending += ch;
      continue;
    }
    const arabic = arabicLetter.test(ch);
    const last = runs[runs.length - 1];
    if (last && last.arabic === arabic) last.text += ch;
    else runs.push({ text: pending + ch, arabic });
    pending = "";
  }
  if (pending) runs.push({ text: pending, arabic: false });
  return runs;
}

/**
 * Emphasised draws text as emphasis, in an <em> per script run. No `dir`: the
 * block around it resolves the direction (CLAUDE.md, `dir="auto"` does not
 * nest), and an <em> carries none of its own.
 */
export function Emphasised({ text }: { text: string }): ReactNode {
  return (
    <>
      {scriptRuns(text).map((run, i) => (
        <em key={i} data-testid="emphasis" className={run.arabic ? "emph emph-arabic" : "emph"}>
          {run.text}
        </em>
      ))}
    </>
  );
}

const encoder = new TextEncoder();

/**
 * spanRanges is where byte spans of the body fall in `text`, which starts at
 * byte `start`, as UTF-16 `[from, to)` ranges clipped to it, in order.
 */
export function spanRanges(text: string, start: number, spans: Spans): [number, number][] {
  const out: [number, number][] = [];
  if (!spans.length) return out;
  const end = start + encoder.encode(text).length;
  for (const [a, b] of spans) {
    if (b <= start || a >= end) continue;
    let from = -1;
    let to = -1;
    let at = 0;
    let byte = start;
    for (const ch of text) {
      if (byte >= a && byte < b) {
        if (from < 0) from = at;
        to = at + ch.length;
      }
      byte += encoder.encode(ch).length;
      at += ch.length;
    }
    if (from >= 0) out.push([from, to]);
  }
  return out.sort((x, y) => x[0] - y[0]);
}

/** How a stretch of text is drawn: plain, italic, or heavier (Arabic script). */
export type EmphasisStyle = "" | "italic" | "heavy";

/**
 * emphasisSegments is `text` cut the way it is drawn: plain stretches, and
 * emphasised ones in script runs. The rule both readers draw, pinned by
 * testdata/reader/emphasis.json.
 */
export function emphasisSegments(
  text: string,
  start: number,
  spans: Spans,
): { text: string; style: EmphasisStyle }[] {
  const out: { text: string; style: EmphasisStyle }[] = [];
  let at = 0;
  for (const [from, to] of spanRanges(text, start, spans)) {
    if (from > at) out.push({ text: text.slice(at, from), style: "" });
    for (const run of scriptRuns(text.slice(Math.max(from, at), to))) {
      out.push({ text: run.text, style: run.arabic ? "heavy" : "italic" });
    }
    at = Math.max(at, to);
  }
  if (at < text.length) out.push({ text: text.slice(at), style: "" });
  return out;
}
